import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAuth } from '@/shared/context/AuthProvider';
import type { RootState } from '@/store';
import { setPortfolioOverview } from '@/features/portfolio/portfolioSlice';
import type {
  UnitProjection,
  ProcessProjection,
  TaskProjection,
  ProjectProjection,
  PortfolioOverviewResponse,
} from '@/features/portfolio/portfolioSlice';

export type {
  UnitProjection,
  ProcessProjection,
  TaskProjection,
  ProjectProjection,
  PortfolioOverviewResponse,
};

// Same resolution used by the other API callers (CreateShareDialog / ProcessSettingsProvider).
const getApiBaseUrl = () => {
  if (import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === 'true') {
    return 'http://127.0.0.1:5001/urbanmix-dev/us-central1/api';
  }
  const apiServer = import.meta.env.VITE_API_SERVER;
  return apiServer ? `${apiServer}/api` : '/api';
};

export interface UsePortfolioOverviewResult {
  data: PortfolioOverviewResponse | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

/**
 * Fetches the compact, server-aggregated portfolio overview for a set of projects.
 * Replaces the client's per-project fetch of all units/processes/actions (and the
 * live processes subscription). On-demand with a manual refresh.
 */
export function usePortfolioOverview(
  projectIds: string[]
): UsePortfolioOverviewResult {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Stable key so the effect only re-runs when the set of projects changes.
  const key = useMemo(
    () => [...projectIds].filter(Boolean).sort().join(','),
    [projectIds]
  );

  // Served from the Redux cache so navigating away and back doesn't refetch.
  const cachedEntry = useSelector((s: RootState) => s.portfolio.byKey[key]);
  const data = cachedEntry?.data ?? null;

  const fetchOverview = useCallback(async () => {
    if (!user || !key) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = await user.getIdToken();
      const url = `${getApiBaseUrl()}/portfolio/overview?projectIds=${encodeURIComponent(
        key
      )}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Failed to load portfolio overview: ${response.status}`);
      }

      const result: PortfolioOverviewResponse = await response.json();
      dispatch(setPortfolioOverview({ key, data: result, fetchedAt: Date.now() }));
    } catch (err) {
      console.error('Error fetching portfolio overview:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [user, key, dispatch]);

  // Only fetch when we don't already have this portfolio cached. Refresh()
  // re-fetches on demand and updates the cache.
  useEffect(() => {
    if (!cachedEntry) {
      fetchOverview();
    }
  }, [cachedEntry, fetchOverview]);

  return { data, loading, error, refresh: fetchOverview };
}
